<script setup lang="ts">
/** AI gateway for everyone with ai.exec: personal keys, Claude Code setup, how to call /ai/v1 (OpenAI-compatible), the models and aliases, own recent requests. */
import { Bot, Code2, KeyRound, ListTree, Plus, Trash2 } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, h, reactive, ref } from "vue";
import { toast } from "vue-sonner";
import { createMyAiKey, revokeMyAiKey, useAiRequests, useMyAiKeys, useMyGateway, type AiRequest, type CreatedKey } from "@/api/gateway";
import ClaudeCodeCard from "@/components/ai-gateway/ClaudeCodeCard.vue";
import CodeBlock from "@/components/common/CodeBlock.vue";
import CopyField from "@/components/common/CopyField.vue";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FormError from "@/components/common/FormError.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs, { type PageTabItem } from "@/components/common/PageTabs.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import TagInput from "@/components/common/TagInput.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { confirm } from "@/composables/useConfirm";
import { errorMessage } from "@/lib/errors";
import { formatDateTime, formatNumber, formatUsd } from "@/lib/format";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "AI gateway", requires: { capability: "ai.exec" }, layout: "wide" } });

const queryClient = useQueryClient();
const keys = useMyAiKeys();
const gateway = useMyGateway();
const requests = useAiRequests(() => ({ limit: 30 }), "me");
const baseUrl = computed(() => gateway.data.value?.baseUrl ?? keys.data.value?.baseUrl ?? `${window.location.origin}/ai/v1`);

const createOpen = ref(false);
const busy = ref(false);
const formError = ref<unknown>(null);
const created = ref<CreatedKey | null>(null);
const form = reactive({ name: "", tags: [] as string[], models: [] as string[], budgetUsd: "", budgetPeriod: "month", rpm: "", expiresInDays: "90" });

function openCreate() {
  Object.assign(form, { name: "", tags: [], models: [], budgetUsd: "", budgetPeriod: "month", rpm: "", expiresInDays: "90" });
  formError.value = null;
  created.value = null;
  createOpen.value = true;
}

async function create() {
  busy.value = true;
  formError.value = null;
  try {
    created.value = await createMyAiKey({
      name: form.name.trim(),
      ...(form.tags.length ? { tags: form.tags } : {}),
      ...(form.models.length ? { models: form.models } : {}),
      ...(form.budgetUsd !== "" ? { budgetUsd: Number(form.budgetUsd), budgetPeriod: form.budgetPeriod as "day" | "week" | "month" } : {}),
      ...(form.rpm !== "" ? { rpm: Number(form.rpm) } : {}),
      expiresInDays: Number(form.expiresInDays),
    });
    await queryClient.invalidateQueries({ queryKey: ["me", "ai-keys"] });
  } catch (err) {
    formError.value = err;
  } finally {
    busy.value = false;
  }
}

async function revoke(id: string, name: string) {
  if (!(await confirm({ title: `Revoke "${name}"?`, description: "Programs using it get 401 right away.", destructive: true, confirmLabel: "Revoke" }))) return;
  try {
    await revokeMyAiKey(id);
    toast.success("Key revoked.");
    await queryClient.invalidateQueries({ queryKey: ["me", "ai-keys"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

const token = computed(() => created.value?.token ?? "smk-…");
const curl = computed(() => `curl ${baseUrl.value}/chat/completions \\\n  -H "Authorization: Bearer ${token.value}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"model": "smake/auto", "messages": [{"role": "user", "content": "Hello"}]}'`);
const env = computed(() => `export OPENAI_BASE_URL=${baseUrl.value}\nexport OPENAI_API_KEY=${token.value}`);
const python = computed(() => `from openai import OpenAI\n\nclient = OpenAI(base_url="${baseUrl.value}", api_key="${token.value}")\nr = client.chat.completions.create(model="smake/auto", messages=[{"role": "user", "content": "Hello"}])\nprint(r.choices[0].message.content)`);

const SNIPPETS: PageTabItem[] = [
  { id: "env", label: "Environment" },
  { id: "curl", label: "curl" },
  { id: "python", label: "Python" },
];
const snippet = ref("env");
const snippetCode = computed(() => (snippet.value === "curl" ? curl.value : snippet.value === "python" ? python.value : env.value));
const snippetTitle = computed(() => (snippet.value === "python" ? "Python (openai SDK)" : snippet.value === "curl" ? "Terminal" : "Terminal — OpenAI SDKs and most tools"));

const aliases = computed(() => (gateway.data.value?.models ?? []).filter((m) => m.id.startsWith("smake/")));
const direct = computed(() => (gateway.data.value?.models ?? []).filter((m) => !m.id.startsWith("smake/")));
const periodOptions = [{ value: "day", label: "per day" }, { value: "week", label: "per week" }, { value: "month", label: "per month" }];

/** A key's limits and use in one muted line. */
function keyLine(k: NonNullable<typeof keys.data.value>["items"][number]): string {
  return [
    k.budgetUsd !== null ? `${formatUsd(k.budgetUsd)}/${k.budgetPeriod}` : "",
    k.rpm ? `${k.rpm} rpm` : "",
    k.models.length ? k.models.join(", ") : "",
    k.expiresAt ? `expires ${formatDateTime(k.expiresAt)}` : "",
    k.lastUsedAt ? `used ${formatDateTime(k.lastUsedAt)}` : "never used",
  ].filter(Boolean).join(" · ");
}

const callColumns: Column<AiRequest>[] = [
  {
    id: "model", header: "Model", accessorFn: (r) => `${r.provider}/${r.model}`, meta: { mobile: "title" },
    cell: ({ row }) => h("span", { class: "font-mono text-xs" }, [h("span", { class: "text-muted-foreground" }, `${row.original.provider}/`), row.original.model]),
  },
  { id: "task", header: "Task", accessorFn: (r) => r.task, meta: { mobile: "subtitle" }, cell: ({ row }) => h("span", { class: "font-mono text-xs" }, row.original.task) },
  {
    id: "result", header: "Result", accessorFn: (r) => `${r.status} ${r.cache ?? ""}`, meta: { mobile: "badge" },
    cell: ({ row }) => {
      const r = row.original;
      return h("span", { class: "inline-flex flex-wrap items-center gap-1" }, [
        r.status === "error" ? h(Badge, { variant: "danger", title: r.error ?? undefined }, () => "error") : h(Badge, { variant: "success", class: "font-normal" }, () => "ok"),
        r.cache === "hit" ? h(Badge, { variant: "info", class: "font-normal" }, () => "cache") : null,
        r.rule ? h("span", { class: "text-xs text-muted-foreground" }, `${r.rule}${r.tier ? ` / ${r.tier}` : ""}`) : null,
      ]);
    },
  },
  { id: "client", header: "Via", accessorFn: (r) => r.client ?? "", meta: { mobile: "hidden" }, cell: ({ row }) => h("span", { class: "text-xs text-muted-foreground" }, [row.original.client, row.original.keyName].filter(Boolean).join(" · ") || "—") },
  {
    id: "tokens", header: "Tokens", accessorFn: (r) => r.inputTokens + r.outputTokens, meta: { align: "end", mobile: "hidden" },
    cell: ({ row }) => h("span", { class: "text-xs tabular-nums text-muted-foreground", title: `${formatNumber(row.original.inputTokens)} in · ${formatNumber(row.original.outputTokens)} out` }, formatNumber(row.original.inputTokens + row.original.outputTokens)),
  },
  { id: "cost", header: "Cost", accessorFn: (r) => r.costUsd, meta: { align: "end" }, cell: ({ row }) => h("span", { class: "text-xs font-medium tabular-nums" }, formatUsd(row.original.costUsd)) },
  {
    id: "at", header: "When", accessorFn: (r) => r.at, meta: { align: "end" },
    cell: ({ row }) => h(RelativeTime, { value: row.original.at, class: "text-xs whitespace-nowrap text-muted-foreground" }),
  },
];
</script>

<template>
  <div class="flex flex-col gap-6">
    <PageHeader title="AI gateway" description="One endpoint for every model smake-mcp can reach, OpenAI-compatible and for Claude Code: smart routing, fallbacks, caching, your own budget. Use it from Claude Code, scripts, tools and editors.">
      <template #actions>
        <Button @click="openCreate"><Plus /> New key</Button>
      </template>
    </PageHeader>

    <div class="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(22rem,26rem)]">
      <!-- Main: Claude Code, scripts -->
      <div class="flex min-w-0 flex-col gap-6">
        <ClaudeCodeCard :token="created?.token" />

        <SectionCard title="Use it from scripts and tools" :icon="Code2" description="OpenAI-compatible: any OpenAI SDK or tool with a configurable base URL. Anthropic SDKs use the base URL in the Claude Code section.">
          <div class="flex flex-col gap-4">
            <div class="flex flex-col gap-1.5"><Label>Base URL</Label><CopyField :value="baseUrl" label="base URL" /></div>
            <div class="flex min-w-0 flex-col gap-2">
              <PageTabs v-model="snippet" :items="SNIPPETS" variant="segmented" label="Example" id-prefix="gateway-snippet" class="self-start" />
              <CodeBlock role="tabpanel" :aria-labelledby="`gateway-snippet-${snippet}`" :code="snippetCode" :title="snippetTitle" :copy-label="`Copy the ${snippet} example`" />
            </div>
            <div class="rounded-lg bg-surface-sunken p-3 text-xs leading-5 text-muted-foreground">
              <p>
                <span class="font-medium text-foreground">Optional headers:</span> <span class="font-mono">x-smake-tags: team:support</span> (routing rules, reports), <span class="font-mono">x-smake-cache-ttl: 3600</span>,
                <span class="font-mono">x-smake-no-cache: 1</span>, <span class="font-mono">x-smake-task: my-script</span> (a label: the call counts as task gateway-my-script, on your gateway budget).
              </p>
              <p class="mt-1">
                <span class="font-medium text-foreground">Responses carry</span> <span class="font-mono">x-smake-model</span>, <span class="font-mono">x-smake-rule</span>, <span class="font-mono">x-smake-cache</span> and
                <span class="font-mono">x-smake-cost-usd</span>.
              </p>
            </div>
          </div>
        </SectionCard>
      </div>

      <!-- Aside: keys, models -->
      <div class="flex min-w-0 flex-col gap-6">
        <SectionCard title="Your keys" :icon="KeyRound" description="A key acts as you: your permissions and AI budgets apply, plus the key's own limits. MCP and API access tokens work too.">
          <template #badge><span v-if="keys.data.value?.items.length" class="text-xs text-muted-foreground tabular-nums">{{ keys.data.value.items.length }}</span></template>
          <ListSkeleton v-if="keys.isPending.value" :rows="2" />
          <ErrorState v-else-if="keys.error.value" compact :error="keys.error.value" @retry="keys.refetch()" />
          <template v-else-if="keys.data.value">
            <EmptyState v-if="!keys.data.value.items.length" :icon="KeyRound" title="No keys yet" description="Create one to call the gateway from your machine, a script or n8n." compact>
              <Button size="sm" @click="openCreate"><Plus /> New key</Button>
            </EmptyState>
            <ul v-else class="-mx-2 flex flex-col">
              <li v-for="k in keys.data.value.items" :key="k.id" class="group/key flex items-start gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-state-hover">
                <span class="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground" aria-hidden="true"><KeyRound class="size-4" /></span>
                <div class="min-w-0 flex-1">
                  <p class="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span class="text-sm font-medium">{{ k.name }}</span>
                    <span class="font-mono text-xs text-muted-foreground">{{ k.prefix }}…</span>
                    <Badge v-if="k.scope !== 'personal'" variant="info" class="font-normal">{{ k.scope }}{{ k.scopeRef ? ` ${k.scopeRef}` : "" }}</Badge>
                    <Badge v-for="tg in k.tags" :key="tg" variant="outline" class="font-normal">{{ tg }}</Badge>
                  </p>
                  <p class="mt-0.5 text-xs text-pretty text-muted-foreground">{{ keyLine(k) }}</p>
                </div>
                <Button size="icon-sm" variant="ghost" class="shrink-0 text-muted-foreground hover:text-destructive" :aria-label="`Revoke ${k.name}`" title="Revoke" @click="revoke(k.id, k.name)"><Trash2 /></Button>
              </li>
            </ul>
          </template>
        </SectionCard>

        <SectionCard title="Models" :icon="Bot" description="Aliases pick the model for you; provider/model ids call one model directly (no fallbacks).">
          <ListSkeleton v-if="gateway.isPending.value" :rows="3" />
          <ErrorState v-else-if="gateway.error.value" compact :error="gateway.error.value" @retry="gateway.refetch()" />
          <template v-else-if="gateway.data.value">
            <ul class="flex flex-col gap-2">
              <li v-for="m in aliases" :key="m.id" class="rounded-lg border px-3 py-2">
                <p class="font-mono text-[13px] font-medium">{{ m.id }}</p>
                <p v-if="m.description" class="mt-0.5 text-xs text-pretty text-muted-foreground">{{ m.description }}</p>
              </li>
            </ul>
            <div v-if="direct.length" class="mt-4">
              <p class="eyebrow mb-2">Direct</p>
              <div class="flex flex-wrap gap-1">
                <Badge v-for="m in direct" :key="m.id" variant="outline" class="font-mono font-normal">{{ m.id }}</Badge>
              </div>
            </div>
          </template>
        </SectionCard>
      </div>
    </div>

    <SectionCard title="Your recent AI calls" :icon="ListTree" description="All your AI use: gateway, MCP tools, jobs and the web app (the latest 30).">
      <ListSkeleton v-if="requests.isPending.value" :rows="4" />
      <ErrorState v-else-if="requests.error.value" compact :error="requests.error.value" @retry="requests.refetch()" />
      <template v-else-if="requests.data.value">
        <EmptyState v-if="!requests.data.value.items.length" :icon="ListTree" title="No AI calls yet" description="Calls through the gateway, Schorschi, MCP tools and jobs show up here with model, cache and cost." compact />
        <DataTable v-else :data="requests.data.value.items" :columns="callColumns" :row-key="(r) => r.id" dense caption="Your recent AI calls" />
      </template>
    </SectionCard>

    <Dialog v-model:open="createOpen">
      <DialogContent class="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{{ created ? "Your new key" : "New gateway key" }}</DialogTitle>
          <DialogDescription>{{ created ? "Copy it now: it is not shown again." : "Limits are optional; your own AI budgets always apply." }}</DialogDescription>
        </DialogHeader>
        <div v-if="created" class="flex flex-col gap-3">
          <CopyField :value="created.token" label="key" />
          <CodeBlock :code="env" title="Terminal" copy-label="Copy the environment" />
          <p class="text-xs text-muted-foreground">For Claude Code: the "Use with Claude Code" section on this page now shows the settings with this key.</p>
        </div>
        <form v-else id="new-key" class="flex flex-col gap-3" @submit.prevent="create">
          <div class="flex flex-col gap-1.5"><Label for="key-name">Name</Label><Input id="key-name" v-model="form.name" required maxlength="120" placeholder="laptop, n8n, my-script" /></div>
          <div class="flex flex-col gap-1.5"><Label>Tags</Label><TagInput v-model="form.tags" placeholder="team:support" aria-label="Tags" /></div>
          <div class="flex flex-col gap-1.5"><Label>Only these models (globs)</Label><TagInput v-model="form.models" placeholder="smake/*" aria-label="Models" /></div>
          <div class="grid grid-cols-[1fr_9rem] gap-2">
            <div class="flex flex-col gap-1.5"><Label for="key-budget">Budget (USD)</Label><Input id="key-budget" v-model="form.budgetUsd" type="number" min="0" step="any" placeholder="none" /></div>
            <div class="flex flex-col gap-1.5"><Label>Period</Label><SimpleSelect v-model="form.budgetPeriod" :options="periodOptions" aria-label="Budget period" /></div>
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div class="flex flex-col gap-1.5"><Label for="key-rpm">Requests per minute</Label><Input id="key-rpm" v-model="form.rpm" type="number" min="1" placeholder="no limit" /></div>
            <div class="flex flex-col gap-1.5"><Label for="key-exp">Expires in days</Label><Input id="key-exp" v-model="form.expiresInDays" type="number" min="1" max="366" /></div>
          </div>
          <FormError :error="formError" />
        </form>
        <DialogFooter>
          <Button v-if="created" @click="createOpen = false">Done</Button>
          <template v-else>
            <Button variant="ghost" @click="createOpen = false">Cancel</Button>
            <Button type="submit" form="new-key" :loading="busy" :disabled="!form.name.trim()">Create key</Button>
          </template>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
